import api from "../../../helpers/axios";

// Superadmin-only API for per-facility file templates (GPI-1256).
// Backend: GS-Backend app/api/routes/facility_file_formats.py
const BASE = "/facility_file_formats";

class FileFormatService {
  async getSchema() {
    const response = await api.get(`${BASE}/schema`);
    return response.data;
  }

  // { facility_id, file_formatting_enabled, file_template_selection_enabled, templates: [...] }
  async getFacilityTemplates(facilityNumber) {
    const response = await api.get(`${BASE}/${facilityNumber}`);
    return response.data;
  }

  async setFormattingEnabled(facilityNumber, enabled) {
    const response = await api.put(`${BASE}/${facilityNumber}/enabled`, { enabled });
    return response.data;
  }

  // Whether the facility's users may pick a template in chat.
  async setSelectionEnabled(facilityNumber, enabled) {
    const response = await api.put(`${BASE}/${facilityNumber}/selection`, { enabled });
    return response.data;
  }

  // Enhanced design: templated files designed by skills, then wrapped in
  // the template (slower) — or rendered locally from the template (fast).
  async setSkillsEnabled(facilityNumber, enabled) {
    const response = await api.put(`${BASE}/${facilityNumber}/skills`, { enabled });
    return response.data;
  }

  async createTemplate(facilityNumber, { fileFormat, name, description, isDefault = false }) {
    const response = await api.post(`${BASE}/${facilityNumber}/templates`, {
      file_format: fileFormat,
      name,
      description: description || null,
      is_default: isDefault,
    });
    return response.data;
  }

  // Partial update: only the keys present in `changes` are applied.
  async updateTemplate(facilityNumber, templateId, changes) {
    const response = await api.put(`${BASE}/${facilityNumber}/templates/${templateId}`, changes);
    return response.data;
  }

  async deleteTemplate(facilityNumber, templateId) {
    await api.delete(`${BASE}/${facilityNumber}/templates/${templateId}`);
  }

  async setDefault(facilityNumber, templateId, isDefault) {
    const url = `${BASE}/${facilityNumber}/templates/${templateId}/default`;
    const response = isDefault ? await api.put(url) : await api.delete(url);
    return response.data;
  }

  async uploadTemplateFile(facilityNumber, templateId, file, { keepOutline = false } = {}) {
    const formData = new FormData();
    formData.append("file", file);
    const response = await api.post(`${BASE}/${facilityNumber}/templates/${templateId}/template`, formData, {
      params: { keep_outline: keepOutline },
    });
    return response.data;
  }

  async downloadTemplateFile(facilityNumber, templateId) {
    const response = await api.get(`${BASE}/${facilityNumber}/templates/${templateId}/template`, {
      responseType: "blob",
    });
    return response.data;
  }

  async deleteTemplateFile(facilityNumber, templateId) {
    const response = await api.delete(`${BASE}/${facilityNumber}/templates/${templateId}/template`);
    return response.data;
  }

  // Template file with the text replacements baked in as {{TOKENS}} (unfilled),
  // reusable on any environment. `replacementsApplied` is 0 when nothing
  // matched (the original file is returned).
  async downloadTokenizedTemplate(facilityNumber, templateId) {
    const response = await api.get(`${BASE}/${facilityNumber}/templates/${templateId}/template/tokenized`, {
      responseType: "blob",
    });
    const applied = Number(response.headers?.["x-replacements-applied"]);
    return { blob: response.data, replacementsApplied: Number.isNaN(applied) ? null : applied };
  }

  async exportConfig(facilityNumber, templateId) {
    const response = await api.get(`${BASE}/${facilityNumber}/templates/${templateId}/config`);
    return response.data;
  }

  // Replaces style, outline, instructions and description; the name, file,
  // default flag and enable switch are left unchanged.
  async importConfig(facilityNumber, templateId, config) {
    const response = await api.post(`${BASE}/${facilityNumber}/templates/${templateId}/config`, config);
    return response.data;
  }

  // Renders sample content (or `content`) with the saved config, or with a
  // draft `styleConfig` that is not saved. Returns the file as a Blob.
  async preview(facilityNumber, templateId, { styleConfig, content, useSavedTemplate = true } = {}) {
    const body = { use_saved_template: useSavedTemplate };
    if (styleConfig !== undefined) body.style_config = styleConfig;
    if (content !== undefined) body.content = content;
    const response = await api.post(`${BASE}/${facilityNumber}/templates/${templateId}/preview`, body, {
      responseType: "blob",
    });
    return response.data;
  }
}

export default new FileFormatService();
